import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CurrencyInr, Plus, Trash } from '@phosphor-icons/react';
import { notify } from '../lib/notify';
import { confirm } from './ConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const monthLabel = (m) => (m.startsWith('0000') ? 'Always' : new Date(`${m}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }));

// VP+ only: salary history (amount + effective-from month) on a team member's profile.
export default function SalaryCard({ userId }) {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ amount: '', month: thisMonth() });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/team/members/${userId}/salary`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [userId]);

  const put = async (monthly_salary, effective_from) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/team/members/${userId}/salary`, { monthly_salary, effective_from });
      setData(r.data); setAdding(false); setForm({ amount: '', month: thisMonth() });
      notify('Salary saved', 'success');
    } catch (e) {
      notify(e.response?.data?.detail || 'Could not save salary', 'error');
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (await confirm({ title: 'Remove salary entry?', message: `Remove ${inr(p.monthly_salary)} from ${monthLabel(p.from_month)}?`, confirmText: 'Remove' })) put(null, p.from_month);
  };

  if (!data) return null;
  return (
    <div className="medical-card p-4 mb-6" data-testid="salary-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#E2F3F0] text-[#00a69c] flex items-center justify-center"><CurrencyInr size={18} /></div>
          <div>
            <p className="text-xs text-[#6D8E87]">Current monthly salary (CTC) · visible to VP & President only</p>
            <p className="text-lg font-bold text-[#052A23]" data-testid="salary-value">{data.monthly_salary == null ? 'Not entered' : inr(data.monthly_salary)}</p>
          </div>
        </div>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#00a69c] hover:underline" data-testid="salary-add-btn">
            <Plus size={12} /> {data.periods.length ? 'Add salary change' : 'Enter salary'}
          </button>
        )}
      </div>
      {adding && (
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="₹ per month"
            className="w-40 px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm" data-testid="salary-input" />
          <span className="text-xs text-[#6D8E87]">effective from</span>
          <input type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })}
            className="px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm" data-testid="salary-month-input" />
          <button type="button" disabled={saving || form.amount === '' || !form.month} onClick={() => put(Number(form.amount), form.month)}
            className="px-3 py-2 rounded-lg bg-[#00a69c] text-white text-xs font-semibold disabled:opacity-60" data-testid="salary-save-btn">{saving ? 'Saving…' : 'Save'}</button>
          <button type="button" onClick={() => { setAdding(false); setForm({ amount: '', month: thisMonth() }); }} className="px-3 py-2 rounded-lg border border-[#D5E8E4] text-xs" data-testid="salary-cancel-btn">Cancel</button>
        </div>
      )}
      {data.periods.length > 0 && (
        <ul className="mt-3 divide-y divide-[#EEF5F3] text-sm" data-testid="salary-periods">
          {data.periods.slice().reverse().map((p) => (
            <li key={p.from_month} className="flex items-center justify-between py-1.5" data-testid={`salary-period-${p.from_month}`}>
              <span className="text-[#3B5A54]">From <b>{monthLabel(p.from_month)}</b></span>
              <span className="flex items-center gap-3"><b className="tabular-nums">{inr(p.monthly_salary)}</b>
                <button type="button" onClick={() => remove(p)} className="text-rose-600" data-testid={`salary-remove-${p.from_month}`}><Trash size={14} /></button></span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
